import { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import Page from "../../../../components/Page";
import AlertMessage from "../../../../components/alert/alert";
import financialService from "../services/financialService";

const PRODUCTS = [
  { key: "extraClean", label: "EXTRA CLEAN" },
  { key: "class", label: "CLASS" },
  { key: "goldGreen", label: "GOLD VERDE" },
  { key: "goldGold", label: "GOLD DOURADO" },
  { key: "superOpto", label: "SUPER OPTO" },
  { key: "digital", label: "DIGITAL" },
];

const MONTHS = [
  { value: 1, label: "Janeiro" },
  { value: 2, label: "Fevereiro" },
  { value: 3, label: "Março" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Maio" },
  { value: 6, label: "Junho" },
  { value: 7, label: "Julho" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Setembro" },
  { value: 10, label: "Outubro" },
  { value: 11, label: "Novembro" },
  { value: 12, label: "Dezembro" },
];

const today = new Date();
const CURRENT_YEAR = today.getFullYear();
const YEARS = Array.from({ length: CURRENT_YEAR - 2019 }, (_, index) => 2020 + index);

const getMonthRange = (month: number, year: number) => {
  const start = new Date(year, month - 1, 1, 0, 0, 0);
  const end = new Date(year, month, 0, 23, 59, 59);

  return {
    start: Math.floor(start.getTime() / 1000),
    end: Math.floor(end.getTime() / 1000),
  };
};

const HEADER_BG = "#002060";
const HEADER_FG = "#FFFFFF";
const ROW_ALT = "#F4F7FB";
const NAVY = "#004A78";

const formatCurrency = (value: number | null | undefined) => {
  if (value == null || Number(value) === 0) return "—";
  return Number(value).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

const formatPairs = (value: number | null | undefined) => {
  if (!value) return "—";
  return Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
};

const formatPercent = (value: number | null | undefined) =>
  value == null
    ? "—"
    : `${(Number(value) * 100).toLocaleString("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })}%`;

const formatVariation = (value: number | null | undefined) => {
  if (value == null) return "—";
  const label = formatPercent(value);
  if (value > 0) return `↑ ${label}`;
  if (value < 0) return `↓ ${label}`;
  return label;
};

const variationCellSx = (value: number | null | undefined) => {
  const base = {
    fontWeight: 700,
    whiteSpace: "nowrap",
    px: 1,
  };
  if (value == null || value === 0) return base;
  if (value > 0) {
    return { ...base, color: "#1B7A3D", bgcolor: "#C8E6C9" };
  }
  return { ...base, color: "#B71C1C", bgcolor: "#FFCDD2" };
};

const headSx = {
  bgcolor: `${HEADER_BG} !important`,
  color: HEADER_FG,
  fontWeight: 700,
  fontSize: 10,
  letterSpacing: "0.02em",
  lineHeight: 1.1,
  py: 0.35,
  px: 0.75,
  height: 26,
  borderBottom: "1px solid #163A6B",
  whiteSpace: "nowrap",
  "& .MuiTableSortLabel-root": {
    color: `${HEADER_FG} !important`,
    fontSize: 10,
    lineHeight: 1.1,
  },
  "& .MuiTableSortLabel-root:hover": {
    color: "#D6E4FF",
  },
  "& .MuiTableSortLabel-icon": {
    color: "rgba(255,255,255,0.7) !important",
    fontSize: 14,
    marginLeft: "2px",
  },
};

const stickyHead = (left: number, zIndex = 6) => ({
  ...headSx,
  position: "sticky",
  left,
  zIndex,
});

const stickyBody = (left: number, bg: string, zIndex = 3) => ({
  position: "sticky",
  left,
  zIndex,
  bgcolor: bg,
  whiteSpace: "nowrap",
});

const numericSx = {
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
  px: 1,
  py: 0.75,
  fontSize: 13,
  color: "#1F2933",
};

const MONTH_ABBR = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

const PAIR_FORMAT = '_-* #,##0.0_-;\\-* #,##0.0_-;_-* "-"??_-;_-@_-';
const MONEY_FORMAT = '_-* #,##0.00_-;\\-* #,##0.00_-;_-* "-"??_-;_-@_-';
const PREMIUM_FORMAT = "0.0%";
const VARIATION_FORMAT = "0%";

const BRANCH_EXPORT_ORDER: Record<string, number> = {
  NE: 1,
  SP: 2,
  SUL: 3,
  PA: 3,
  SC: 3,
  DF: 4,
};

const exportBranch = (branch: string | null | undefined) => {
  if (branch === "PA" || branch === "SC") return "SUL";
  return branch || "";
};

const exportCustomerName = (row: any) => {
  const code = row.customerCode || row.customerInitials;
  const name = row.customerName || "";
  if (code && name) return `${code} - ${name}`;
  return name || code || "";
};

const exportCustomerCode = (row: any) => {
  if (row.sigaCode) return String(row.sigaCode).padStart(6, "0");
  if (row.customerId) return String(row.customerId).padStart(6, "0");
  return "";
};

const numberOrZero = (value: number | null | undefined) =>
  value == null || Number.isNaN(Number(value)) ? 0 : Number(value);

const buildRankingArSheet = (rows: any[], month: number, year: number) => {
  const monthLabel = (MONTHS.find((item) => item.value === month)?.label || "").toUpperCase();
  const headerRow1 = [
    `${monthLabel} - ${year}`,
    null,
    "OPTO EXTRA CLEAN",
    null,
    "OPTO CLASS",
    null,
    "OPTO GOLD VERDE",
    null,
    "OPTO GOLD DOURADO",
    null,
    "SUPER OPTO",
    null,
    "OPTO DIGITAL",
    null,
    "Volume",
    "Preço",
    "Total",
    "%",
    "Variação",
    "Volume",
    null,
    null,
    null,
    null,
  ];
  const headerRow2 = [
    "Filial",
    "Nome",
    "Pares",
    "R$ Unitário",
    "Pares ",
    "R$ Unitário ",
    "Pares  ",
    "R$ Unitário  ",
    "Pares   ",
    "R$ Unitário   ",
    "Pares    ",
    "R$ Unitário    ",
    "Pares     ",
    "R$ Unitário     ",
    "Quinzena",
    "Médio",
    "Faturado",
    "Premium",
    "Q. Anterior",
    "Sub/Desc",
    "Cidade",
    "Nome Fantasia",
    "Cond. Pagto",
    "Cliente",
  ];

  const orderedRows = [...rows].sort((first, second) => {
    const firstBranch = BRANCH_EXPORT_ORDER[exportBranch(first.branch)] || 99;
    const secondBranch = BRANCH_EXPORT_ORDER[exportBranch(second.branch)] || 99;
    if (firstBranch !== secondBranch) return firstBranch - secondBranch;
    return exportCustomerName(first).localeCompare(exportCustomerName(second), "pt-BR");
  });

  const dataRows = orderedRows.map((row) => [
    exportBranch(row.branch),
    exportCustomerName(row),
    ...PRODUCTS.flatMap((product) => {
      const metrics = row.products?.[product.key] || {};
      return [numberOrZero(metrics.pairs), numberOrZero(metrics.unitPrice)];
    }),
    null,
    null,
    null,
    null,
    row.previousVariation == null ? null : Number(row.previousVariation),
    row.quarterVariation == null ? null : Number(row.quarterVariation),
    row.city || "",
    row.tradeName || "",
    row.paymentTerms || "",
    exportCustomerCode(row),
  ]);

  const worksheet = XLSX.utils.aoa_to_sheet([headerRow1, headerRow2, ...dataRows]);

  worksheet["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 1 } },
    { s: { r: 0, c: 2 }, e: { r: 0, c: 3 } },
    { s: { r: 0, c: 4 }, e: { r: 0, c: 5 } },
    { s: { r: 0, c: 6 }, e: { r: 0, c: 7 } },
    { s: { r: 0, c: 8 }, e: { r: 0, c: 9 } },
    { s: { r: 0, c: 10 }, e: { r: 0, c: 11 } },
    { s: { r: 0, c: 12 }, e: { r: 0, c: 13 } },
  ];

  worksheet["!cols"] = [
    { wch: 8 },
    { wch: 66 },
    { wch: 9 },
    { wch: 13 },
    { wch: 9 },
    { wch: 13 },
    { wch: 9 },
    { wch: 13 },
    { wch: 9 },
    { wch: 14 },
    { wch: 10 },
    { wch: 14 },
    { wch: 10 },
    { wch: 14 },
    { wch: 11 },
    { wch: 10 },
    { wch: 12 },
    { wch: 11 },
    { wch: 12 },
    { wch: 11 },
    { wch: 22 },
    { wch: 24 },
    { wch: 16 },
    { wch: 12 },
  ];

  orderedRows.forEach((_, index) => {
    const excelRow = index + 3;
    worksheet[XLSX.utils.encode_cell({ r: excelRow - 1, c: 14 })] = {
      t: "n",
      f: `C${excelRow}+E${excelRow}+G${excelRow}+I${excelRow}+K${excelRow}+M${excelRow}`,
      z: PAIR_FORMAT,
    };
    worksheet[XLSX.utils.encode_cell({ r: excelRow - 1, c: 16 })] = {
      t: "n",
      f: `C${excelRow}*D${excelRow}+E${excelRow}*F${excelRow}+G${excelRow}*H${excelRow}+I${excelRow}*J${excelRow}+K${excelRow}*L${excelRow}+M${excelRow}*N${excelRow}`,
      z: MONEY_FORMAT,
    };
    worksheet[XLSX.utils.encode_cell({ r: excelRow - 1, c: 15 })] = {
      t: "n",
      f: `IF(O${excelRow}=0,0,Q${excelRow}/O${excelRow})`,
      z: MONEY_FORMAT,
    };
    worksheet[XLSX.utils.encode_cell({ r: excelRow - 1, c: 17 })] = {
      t: "n",
      f: `IF(O${excelRow}=0,0,(G${excelRow}+I${excelRow}+K${excelRow}+M${excelRow})/O${excelRow})`,
      z: PREMIUM_FORMAT,
    };

    PRODUCTS.forEach((__, productIndex) => {
      const pairsCell = worksheet[XLSX.utils.encode_cell({ r: excelRow - 1, c: 2 + productIndex * 2 })];
      const priceCell = worksheet[XLSX.utils.encode_cell({ r: excelRow - 1, c: 3 + productIndex * 2 })];
      if (pairsCell) pairsCell.z = PAIR_FORMAT;
      if (priceCell) priceCell.z = MONEY_FORMAT;
    });

    [18, 19].forEach((columnIndex) => {
      const variationCell = worksheet[XLSX.utils.encode_cell({ r: excelRow - 1, c: columnIndex })];
      if (variationCell && variationCell.v != null) {
        variationCell.t = "n";
        variationCell.z = VARIATION_FORMAT;
      }
    });
  });

  if (orderedRows.length) {
    worksheet["!autofilter"] = { ref: `A2:X${orderedRows.length + 2}` };
  }

  worksheet["!views"] = [
    {
      state: "frozen",
      ySplit: 2,
      topLeftCell: "A3",
      activePane: "bottomLeft",
      showGridLines: false,
    },
  ];

  return {
    worksheet,
    sheetName: `${year}.${MONTH_ABBR[month - 1]}`,
    fileName: `Ranking_AR_${year}-${MONTH_ABBR[month - 1]}.xlsx`,
  };
};

export default function RankingAR() {
  const [rows, setRows] = useState<any[]>([]);
  const [summary, setSummary] = useState({ volume: 0, billed: 0, customers: 0 });
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [branch, setBranch] = useState("Todas");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [orderBy, setOrderBy] = useState("billed");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const tableAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const syncFullscreenState = () => {
      setIsFullscreen(document.fullscreenElement === tableAreaRef.current);
    };

    document.addEventListener("fullscreenchange", syncFullscreenState);
    return () => document.removeEventListener("fullscreenchange", syncFullscreenState);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await tableAreaRef.current?.requestFullscreen();
      }
    } catch (error) {
      console.error(error);
      setErrorMessage("Não foi possível abrir a tabela em tela cheia.");
    }
  };

  const handleSort = (property: string) => {
    if (orderBy === property) {
      setOrder(order === "asc" ? "desc" : "asc");
      return;
    }

    setOrderBy(property);
    setOrder(property === "branch" || property === "customer" ? "asc" : "desc");
  };

  const sortedRows = useMemo(() => {
    const readValue = (row: any, property: string): number | string => {
      if (property === "branch") return row.branch || "";
      if (property === "customer") return `${row.customerCode || row.customerInitials || ""} ${row.customerName || ""}`;
      if (property.startsWith("product.")) {
        const [, product, metric] = property.split(".");
        return Number(row.products?.[product]?.[metric] || 0);
      }

      return Number(row[property] || 0);
    };

    return rows
      .map((row, index) => ({ row, index }))
      .sort((first, second) => {
        const firstValue = readValue(first.row, orderBy);
        const secondValue = readValue(second.row, orderBy);
        let comparison = 0;

        if (typeof firstValue === "string" && typeof secondValue === "string") {
          comparison = firstValue.localeCompare(secondValue, "pt-BR");
        } else {
          comparison = Number(firstValue) - Number(secondValue);
        }

        if (comparison === 0) return first.index - second.index;
        return order === "asc" ? comparison : -comparison;
      })
      .map(({ row }) => row);
  }, [rows, orderBy, order]);

  const fetchReport = async () => {
    if (!month || !year) {
      setErrorMessage("Informe o mês e o ano do relatório.");
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");
      const { start, end } = getMonthRange(month, year);
      const response = await financialService.getRankingAR(start, end, branch);

      setRows(Array.isArray(response.data?.rows) ? response.data.rows : []);
      setSummary(response.data?.summary || { volume: 0, billed: 0, customers: 0 });
    } catch (error) {
      console.error(error);
      setRows([]);
      setSummary({ volume: 0, billed: 0, customers: 0 });
      setErrorMessage("Não foi possível carregar o Ranking AR.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // The initial report should load once with the current month defaults.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportSpreadsheet = () => {
    if (!sortedRows.length) {
      setErrorMessage("Não há dados para exportar.");
      return;
    }

    try {
      const workbook = XLSX.utils.book_new();
      const { worksheet, sheetName, fileName } = buildRankingArSheet(sortedRows, month, year);

      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error(error);
      setErrorMessage("Não foi possível exportar a planilha.");
    }
  };

  return (
    <Page title="Ranking AR | Optools">
      <Container
        maxWidth={false}
        disableGutters
        sx={{
          px: 0,
          height: { xs: "auto", lg: "calc(100vh - 40px)" },
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          alignItems={{ lg: "center" }}
          spacing={1.25}
          sx={{ mb: 1, flexWrap: "wrap" }}
        >
          <Typography variant="h6" sx={{ color: NAVY, fontWeight: 700, mr: 1 }}>
            Ranking AR
          </Typography>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Mês</InputLabel>
            <Select
              value={month}
              label="Mês"
              onChange={(event) => setMonth(Number(event.target.value))}
            >
              {MONTHS.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 96 }}>
            <InputLabel>Ano</InputLabel>
            <Select
              value={year}
              label="Ano"
              onChange={(event) => setYear(Number(event.target.value))}
            >
              {YEARS.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Unidade</InputLabel>
            <Select
              value={branch}
              label="Unidade"
              onChange={(event) => setBranch(event.target.value)}
            >
              <MenuItem value="Todas">Todas</MenuItem>
              <MenuItem value="NE">NE</MenuItem>
              <MenuItem value="SP">SP</MenuItem>
              <MenuItem value="PA">PA</MenuItem>
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="contained"
            onClick={fetchReport}
            disabled={loading}
            sx={{ height: 36, backgroundColor: "#004A78", "&:hover": { backgroundColor: "#226C9A" } }}
          >
            {loading ? <CircularProgress size={18} color="inherit" /> : "Filtrar"}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={exportSpreadsheet}
            disabled={loading || rows.length === 0}
            sx={{
              height: 36,
              color: "#004A78",
              borderColor: "#004A78",
              "&:hover": { borderColor: "#226C9A", backgroundColor: "rgba(0,74,120,0.04)" },
            }}
          >
            Exportar
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={toggleFullscreen}
            sx={{ height: 36 }}
          >
            {isFullscreen ? "Sair da tela cheia" : "Tela cheia"}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" spacing={2} sx={{ color: NAVY, alignItems: "baseline" }}>
            <Typography variant="body2">
              <Box component="span" sx={{ color: "text.secondary", mr: 0.5 }}>Clientes</Box>
              <strong>{summary.customers}</strong>
            </Typography>
            <Typography variant="body2">
              <Box component="span" sx={{ color: "text.secondary", mr: 0.5 }}>Volume</Box>
              <strong>{Number(summary.volume || 0).toLocaleString("pt-BR")} pares</strong>
            </Typography>
            <Typography variant="body2">
              <Box component="span" sx={{ color: "text.secondary", mr: 0.5 }}>Faturado</Box>
              <strong>
                {Number(summary.billed || 0).toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })}
              </strong>
            </Typography>
          </Stack>
        </Stack>

        <Box
          ref={tableAreaRef}
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            ...(isFullscreen
              ? { height: "100vh", bgcolor: "#EEF2F6", p: 1.5 }
              : {}),
          }}
        >
          <Card sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            <TableContainer
              sx={{
                height: "100%",
                minHeight: 480,
                overflow: "auto",
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 2100,
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  "& .MuiTableCell-root": {
                    borderColor: "#E5EAF0",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell rowSpan={2} sx={stickyHead(0, 7)}>
                      <TableSortLabel active={orderBy === "branch"} direction={orderBy === "branch" ? order : "asc"} onClick={() => handleSort("branch")}>
                        Filial
                      </TableSortLabel>
                    </TableCell>
                    <TableCell rowSpan={2} sx={{ ...stickyHead(68, 7), minWidth: 280 }}>
                      <TableSortLabel active={orderBy === "customer"} direction={orderBy === "customer" ? order : "asc"} onClick={() => handleSort("customer")}>
                        Cliente
                      </TableSortLabel>
                    </TableCell>
                    {PRODUCTS.map((product) => (
                      <TableCell
                        key={product.key}
                        colSpan={2}
                        align="center"
                        sx={{ ...headSx, borderLeft: "1px solid #163A6B" }}
                      >
                        {product.label}
                      </TableCell>
                    ))}
                    {[
                      ["volume", "Volume"],
                      ["averagePrice", "Médio"],
                      ["billed", "Faturado"],
                      ["premium", "Premium"],
                      ["previousVariation", "Q. Anterior"],
                      ["quarterVariation", "Sub/Desc"],
                    ].map(([key, label]) => (
                      <TableCell key={key} rowSpan={2} align="right" sx={headSx}>
                        <TableSortLabel active={orderBy === key} direction={orderBy === key ? order : "asc"} onClick={() => handleSort(key)}>
                          {label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell rowSpan={2} sx={headSx}>Cidade</TableCell>
                    <TableCell rowSpan={2} sx={headSx}>Nome fantasia</TableCell>
                    <TableCell rowSpan={2} sx={headSx}>Cond. pagto.</TableCell>
                  </TableRow>
                  <TableRow>
                    {PRODUCTS.flatMap((product, index) => [
                      <TableCell
                        key={`${product.key}-pairs`}
                        align="right"
                        sx={{ ...headSx, top: 26, borderLeft: index === 0 ? "1px solid #163A6B" : undefined }}
                      >
                        <TableSortLabel active={orderBy === `product.${product.key}.pairs`} direction={orderBy === `product.${product.key}.pairs` ? order : "asc"} onClick={() => handleSort(`product.${product.key}.pairs`)}>
                          Pares
                        </TableSortLabel>
                      </TableCell>,
                      <TableCell key={`${product.key}-price`} align="right" sx={{ ...headSx, top: 26 }}>
                        <TableSortLabel active={orderBy === `product.${product.key}.unitPrice`} direction={orderBy === `product.${product.key}.unitPrice` ? order : "asc"} onClick={() => handleSort(`product.${product.key}.unitPrice`)}>
                          R$ unit.
                        </TableSortLabel>
                      </TableCell>,
                    ])}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={23} align="center" sx={{ py: 5 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={23} align="center" sx={{ py: 5 }}>
                        Nenhum fechamento AR encontrado no mês selecionado.
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedRows.map((row, rowIndex) => {
                      const bg = rowIndex % 2 === 0 ? "#FFFFFF" : ROW_ALT;
                      const customerLabel = [row.customerCode || row.customerInitials, row.customerName]
                        .filter(Boolean)
                        .join(" - ");

                      return (
                        <TableRow
                          hover
                          key={`${row.customerId}-${row.branch}`}
                          sx={{
                            bgcolor: bg,
                            "&:hover td": { bgcolor: "#E8F1F8" },
                          }}
                        >
                          <TableCell sx={{ ...stickyBody(0, bg), ...numericSx, fontWeight: 700, color: NAVY, width: 68 }}>
                            {row.branch || "—"}
                          </TableCell>
                          <TableCell
                            title={customerLabel}
                            sx={{ ...stickyBody(68, bg), minWidth: 280, maxWidth: 360, pr: 2 }}
                          >
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ fontWeight: 700, color: "#12263A", fontSize: 13 }}
                            >
                              {customerLabel}
                            </Typography>
                          </TableCell>
                          {PRODUCTS.flatMap((product, index) => {
                            const metrics = row.products?.[product.key] || {};
                            return [
                              <TableCell
                                key={`${product.key}-pairs`}
                                align="right"
                                sx={{
                                  ...numericSx,
                                  borderLeft: index === 0 ? "1px solid #E5EAF0" : undefined,
                                  color: metrics.pairs ? "#12263A" : "#9AA5B1",
                                }}
                              >
                                {formatPairs(metrics.pairs)}
                              </TableCell>,
                              <TableCell
                                key={`${product.key}-price`}
                                align="right"
                                sx={{ ...numericSx, color: metrics.unitPrice ? "#12263A" : "#9AA5B1" }}
                              >
                                {formatCurrency(metrics.unitPrice)}
                              </TableCell>,
                            ];
                          })}
                          <TableCell align="right" sx={{ ...numericSx, fontWeight: 700 }}>
                            {formatPairs(row.volume)}
                          </TableCell>
                          <TableCell align="right" sx={numericSx}>{formatCurrency(row.averagePrice)}</TableCell>
                          <TableCell align="right" sx={{ ...numericSx, fontWeight: 800, color: NAVY }}>
                            {formatCurrency(row.billed)}
                          </TableCell>
                          <TableCell align="right" sx={numericSx}>{formatPercent(row.premium)}</TableCell>
                          <TableCell align="right" sx={variationCellSx(row.previousVariation)}>
                            {formatVariation(row.previousVariation)}
                          </TableCell>
                          <TableCell align="right" sx={variationCellSx(row.quarterVariation)}>
                            {formatVariation(row.quarterVariation)}
                          </TableCell>
                          <TableCell sx={{ ...numericSx, color: "#52606D" }}>{row.city || "—"}</TableCell>
                          <TableCell sx={{ ...numericSx, color: "#52606D" }}>{row.tradeName || "—"}</TableCell>
                          <TableCell sx={{ ...numericSx, color: "#52606D" }}>{row.paymentTerms || "—"}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Box>
      </Container>
      {errorMessage && <AlertMessage open message={errorMessage} />}
    </Page>
  );
}
